import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { ActionIcon, Alert, Button, Checkbox, Group, List, Menu, Modal, Select, Stack, Table, Text, Textarea, Tooltip } from '@mantine/core';
import { tenderApi, type ItemInput, type PackageDetail, type PositionType, type TenderItem } from '../../api';
import { LIBRARY_FIELDS, POSITION_TYPES, formatAmount } from '../../format';
import { AmountCell } from '../../ui/AmountCell';
import { EditableText } from '../../ui/EditableText';
import { useContextMenu } from '../../ui/ContextMenu';
import { parsePastedBoq } from './paste';
import { useCan } from '../../core/useMe';
import { GaebIntoLv, LibraryManager, LibraryPicker } from './TextLibraries';
import { blockEnd, depths, suggestOz } from './outline';
import { errorText, useTenderMutation } from './usePackage';

/**
 * The tender bill of quantities. Titles as bold separator rows with their
 * estimated subtotal, subtitles and positions indented below (AP-113); every
 * cell edited in place. The
 * estimated unit price is the planner's expectation, the yardstick for the
 * price comparison later.
 */
export function BoqPanel({ projectId, detail, canEdit }: { projectId: string; detail: PackageDetail; canEdit: boolean }): JSX.Element {
  const canManageLibraries = useCan('library.manage');
  const packageId = detail.package.id;
  const [pasteOpen, setPasteOpen] = useState(false);
  const [longTextOf, setLongTextOf] = useState<TenderItem | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [librariesOpen, setLibrariesOpen] = useState(false);
  const [gaebOpen, setGaebOpen] = useState(false);
  const add = useTenderMutation(projectId, packageId, (body: ItemInput & { afterId?: string | null; parentId?: string | null }) => tenderApi.addItem(projectId, packageId, body));
  const update = useTenderMutation(projectId, packageId, ({ id, ...body }: { id: string } & Partial<ItemInput>) => tenderApi.updateItem(projectId, packageId, id, body));
  const remove = useTenderMutation(projectId, packageId, (id: string) => tenderApi.removeItem(projectId, packageId, id));
  const error = add.error ?? update.error ?? remove.error;
  const items = detail.items;
  const locked = detail.package.status === 'awarded';

  const estimateByTitle = useMemo(() => {
    const sums = new Map<string, number>();
    for (const i of items) {
      if (i.kind !== 'position' || i.estimateUnitPrice === null || !counts(i.positionType)) continue;
      let t = i.parentId;
      while (t) {
        sums.set(t, (sums.get(t) ?? 0) + (i.quantity ?? 0) * i.estimateUnitPrice);
        t = items.find((x) => x.id === t)?.parentId ?? null;
      }
    }
    return sums;
  }, [items]);

  const depth = useMemo(() => depths(items), [items]);
  const indent = (item: TenderItem): string => `calc(${depth.get(item.id) ?? 0} * var(--mantine-spacing-md))`;
  const nextOz = (after: TenderItem | undefined, kind: 'title' | 'position'): string => suggestOz(items, after, kind);
  /** A new title on the level of `sibling` (a title), after its block. */
  const titleBeside = (sibling: TenderItem | undefined, fallbackAfter: TenderItem): void => {
    const parent = sibling?.parentId ? items.find((i) => i.id === sibling.parentId) ?? null : null;
    const after = sibling ? blockEnd(items, sibling.id) ?? fallbackAfter : fallbackAfter;
    add.mutate({ kind: 'title', oz: suggestOz(items, undefined, 'title', parent), shortText: 'Neuer Titel', afterId: after.id, parentId: parent?.id ?? null });
  };
  const context = useContextMenu<TenderItem>((item, close) => (
    <>
      <Menu.Label>{item.oz} {item.shortText.slice(0, 40)}</Menu.Label>
      {canEdit && !locked && (
        <>
          <Menu.Item onClick={() => { close(); add.mutate({ kind: 'position', oz: nextOz(item, 'position'), shortText: 'Neue Position', quantity: 1, unit: 'St', afterId: item.id }); }}>Position darunter einfügen</Menu.Item>
          {item.kind === 'title' && (
            <Menu.Item onClick={() => { close(); add.mutate({ kind: 'title', oz: suggestOz(items, undefined, 'title', item), shortText: 'Neuer Untertitel', afterId: (blockEnd(items, item.id) ?? item).id, parentId: item.id }); }}>Untertitel einfügen</Menu.Item>
          )}
          <Menu.Item onClick={() => { close(); titleBeside(item.kind === 'title' ? item : items.find((i) => i.id === item.parentId), item); }}>Titel darunter einfügen</Menu.Item>
          <Menu.Item onClick={() => { close(); add.mutate({ kind: 'note', oz: '', shortText: 'Vorbemerkung', afterId: item.id }); }}>Vorbemerkung darunter einfügen</Menu.Item>
        </>
      )}
      {item.kind !== 'title' && <Menu.Item onClick={() => { close(); setLongTextOf(item); }}>{item.kind === 'note' ? 'Text der Vorbemerkung' : 'Langtext'} {canEdit && !locked ? 'bearbeiten' : 'ansehen'} …</Menu.Item>}
      {canEdit && !locked && (
        <>
          <Menu.Divider />
          <Menu.Item color="red" onClick={() => { close(); remove.mutate(item.id); }}>{item.kind === 'title' ? 'Titel löschen' : item.kind === 'note' ? 'Vorbemerkung löschen' : 'Position löschen'}</Menu.Item>
        </>
      )}
    </>
  ));

  const editable = canEdit && !locked;
  const last = items[items.length - 1];

  return (
    <Stack gap="sm">
      {error && <Alert color="red">{errorText(error)}</Alert>}
      {locked && <Alert color="gray" py={6}>Die Vergabeeinheit ist vergeben; das LV ist festgeschrieben. Zum Ändern den Zuschlag zurücknehmen.</Alert>}
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {items.filter((i) => i.kind === 'position').length} Positionen · Schätzung {formatAmount(detail.evaluation.estimateTotal)} € netto
          {detail.evaluation.estimateTotal === null && items.some((i) => i.kind === 'position') ? ' (unvollständig: nicht jede Position hat einen geschätzten EP)' : ''}
        </Text>
        <Group gap="xs">
          {editable && (
            <>
              <Button size="xs" variant="light" onClick={() => add.mutate({ kind: 'title', oz: nextOz(last, 'title'), shortText: 'Neuer Titel' })}>+ Titel</Button>
              <Button size="xs" variant="light" onClick={() => add.mutate({ kind: 'position', oz: nextOz(last, 'position'), shortText: 'Neue Position', quantity: 1, unit: 'St' })}>+ Position</Button>
              <Button size="xs" variant="default" onClick={() => setPasteOpen(true)}>Aus Excel einfügen …</Button>
              <Button size="xs" variant="default" onClick={() => setPickerOpen(true)}>Aus Bibliothek …</Button>
              <Button size="xs" variant="default" onClick={() => setGaebOpen(true)}>Aus GAEB-Datei …</Button>
            </>
          )}
          <Button size="xs" variant="subtle" color="gray" onClick={() => setLibrariesOpen(true)}>Textbibliotheken …</Button>
          <Button size="xs" variant="default" component="a" href={tenderApi.lvCsvUrl(projectId, packageId)}>LV als CSV</Button>
          <Button size="xs" variant="default" component="a" href={tenderApi.lvGaebUrl(projectId, packageId)} title="GAEB DA XML 3.2, Phase 83 (Angebotsaufforderung): ohne Preise, mit Vorbemerkungen">LV als GAEB (X83)</Button>
        </Group>
      </Group>

      {items.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="lg">Noch kein Leistungsverzeichnis. Titel und Positionen anlegen oder aus einer Tabelle einfügen.</Text>
      ) : (
        <Table.ScrollContainer minWidth={900}>
          <Table verticalSpacing={3} highlightOnHover className="pst-tabular">
            <Table.Thead>
              <Table.Tr>
                {editable && <Table.Th w={30} />}
                <Table.Th w={90}>OZ</Table.Th>
                <Table.Th>Kurztext</Table.Th>
                <Table.Th w={100} ta="right">Menge</Table.Th>
                <Table.Th w={70}>Einheit</Table.Th>
                <Table.Th w={130}>Art</Table.Th>
                <Table.Th w={110} ta="right" title="geschätzter Einheitspreis netto">Schätz-EP (€)</Table.Th>
                <Table.Th w={120} ta="right" title="Menge × geschätzter EP">Schätz-GP (€)</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((item) =>
                item.kind === 'title' ? (
                  <Table.Tr key={item.id} onContextMenu={(e) => context.open(e, item)} style={{ background: 'var(--mantine-color-default-hover)' }}>
                    {editable && <Table.Td><Del onClick={() => remove.mutate(item.id)} label="Titel löschen" /></Table.Td>}
                    <Table.Td><EditableText value={item.oz} monospace bold onCommit={(oz) => editable && update.mutate({ id: item.id, oz })} /></Table.Td>
                    <Table.Td colSpan={5} pl={indent(item)}><EditableText value={item.shortText} bold onCommit={(shortText) => editable && update.mutate({ id: item.id, shortText })} /></Table.Td>
                    <Table.Td><AmountCell value={estimateByTitle.has(item.id) ? Math.round(estimateByTitle.get(item.id)! * 100) / 100 : null} readOnly bold /></Table.Td>
                  </Table.Tr>
                ) : item.kind === 'note' ? (
                  <Table.Tr key={item.id} onContextMenu={(e) => context.open(e, item)}>
                    {editable && <Table.Td><Del onClick={() => remove.mutate(item.id)} label="Vorbemerkung löschen" /></Table.Td>}
                    <Table.Td><Text size="xs" c="dimmed" fs="italic">Vorbem.</Text></Table.Td>
                    <Table.Td colSpan={6} pl={indent(item)}>
                      <Group gap={4} wrap="nowrap">
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <EditableText value={item.shortText} onCommit={(shortText) => editable && update.mutate({ id: item.id, shortText })} />
                          {item.longText && <Text size="xs" c="dimmed" lineClamp={2} style={{ whiteSpace: 'pre-line' }}>{item.longText}</Text>}
                        </div>
                        <ActionIcon size="xs" variant="subtle" color="gray" style={{ opacity: item.longText ? 1 : 0.35 }} onClick={() => setLongTextOf(item)} aria-label="Text der Vorbemerkung">¶</ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  <Table.Tr key={item.id} onContextMenu={(e) => context.open(e, item)}>
                    {editable && <Table.Td><Del onClick={() => remove.mutate(item.id)} label="Position löschen" /></Table.Td>}
                    <Table.Td><EditableText value={item.oz} monospace dimmed onCommit={(oz) => editable && update.mutate({ id: item.id, oz })} /></Table.Td>
                    <Table.Td pl={indent(item)}>
                      <Group gap={4} wrap="nowrap">
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <EditableText value={item.shortText} onCommit={(shortText) => editable && update.mutate({ id: item.id, shortText })} />
                        </div>
                        <Tooltip label={[item.longText ? item.longText.slice(0, 300) : 'Langtext ergänzen', item.origin ? `${LIBRARY_FIELDS.origin.label}: ${item.origin}` : ''].filter(Boolean).join('\n\n')} multiline w={320} style={{ whiteSpace: 'pre-line' }}>
                          <ActionIcon size="xs" variant="subtle" color="gray" style={{ opacity: item.longText ? 1 : 0.35 }} onClick={() => setLongTextOf(item)} aria-label="Langtext">¶</ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                    <Table.Td><AmountCell value={item.quantity} quantity readOnly={!editable} onCommit={(quantity) => update.mutate({ id: item.id, quantity })} /></Table.Td>
                    <Table.Td><EditableText value={item.unit ?? ''} allowEmpty placeholder=" " onCommit={(unit) => editable && update.mutate({ id: item.id, unit: unit || null })} /></Table.Td>
                    <Table.Td>
                      <Select
                        size="xs"
                        variant="unstyled"
                        disabled={!editable}
                        data={POSITION_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                        value={item.positionType}
                        onChange={(v) => v && v !== item.positionType && update.mutate({ id: item.id, positionType: v as PositionType })}
                        allowDeselect={false}
                        comboboxProps={{ withinPortal: true }}
                      />
                    </Table.Td>
                    <Table.Td><AmountCell value={item.estimateUnitPrice} readOnly={!editable} onCommit={(estimateUnitPrice) => update.mutate({ id: item.id, estimateUnitPrice })} /></Table.Td>
                    <Table.Td>
                      <AmountCell
                        value={item.estimateUnitPrice === null ? null : Math.round((item.quantity ?? 0) * item.estimateUnitPrice * 100) / 100}
                        readOnly
                        dimmed={!counts(item.positionType)}
                        title={counts(item.positionType) ? undefined : 'Nicht in der Summe (Bedarfs- oder Wahlposition)'}
                      />
                    </Table.Td>
                  </Table.Tr>
                ),
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <Text size="xs" c="dimmed">Rechtsklick auf eine Zeile fügt darunter ein – Position, Titel oder Vorbemerkung; auf einem Titel auch einen Untertitel. Gelöschte Titel geben ihre Zeilen an den Übertitel ab. Vorbemerkungen aus einer Bibliothek oder GAEB-Datei kommen mit. Bedarfs- und Wahlpositionen werden bepreist, zählen aber nicht zur Angebotssumme.</Text>

      <PasteModal opened={pasteOpen} onClose={() => setPasteOpen(false)} projectId={projectId} packageId={packageId} hasItems={items.length > 0} />
      <Modal opened={longTextOf !== null} onClose={() => setLongTextOf(null)} title={longTextOf ? (longTextOf.kind === 'note' ? 'Text der Vorbemerkung' : `Langtext ${longTextOf.oz}`) : ''} size="lg">
        {longTextOf && (
          <LongTextForm
            item={longTextOf}
            readOnly={!editable}
            onSave={(longText) => { update.mutate({ id: longTextOf.id, longText }); setLongTextOf(null); }}
          />
        )}
      </Modal>
      {pickerOpen && <LibraryPicker projectId={projectId} packageId={packageId} lvItems={items} onClose={() => setPickerOpen(false)} />}
      {librariesOpen && <LibraryManager canEdit={canManageLibraries} onClose={() => setLibrariesOpen(false)} />}
      {gaebOpen && <GaebIntoLv projectId={projectId} packageId={packageId} lvItems={items} onClose={() => setGaebOpen(false)} />}
      {context.menu}
    </Stack>
  );
}

const counts = (t: PositionType): boolean => t === 'normal' || t === 'lump';

function Del({ onClick, label }: { onClick: () => void; label: string }): JSX.Element {
  return <ActionIcon size="xs" variant="subtle" color="gray" aria-label={label} title={label} onClick={onClick}>✕</ActionIcon>;
}

function LongTextForm({ item, readOnly, onSave }: { item: TenderItem; readOnly: boolean; onSave: (text: string | null) => void }): JSX.Element {
  const [text, setText] = useState(item.longText ?? '');
  return (
    <Stack gap="sm">
      <Text size="sm" fw={500}>{item.shortText}</Text>
      <Textarea autosize minRows={6} maxRows={20} value={text} readOnly={readOnly} onChange={(e) => setText(e.currentTarget.value)} placeholder="Ausführliche Beschreibung der Leistung, eigene Formulierung" />
      {item.origin && <Text size="xs" c="dimmed" title={LIBRARY_FIELDS.origin.hint}>{LIBRARY_FIELDS.origin.label}: {item.origin}</Text>}
      {!readOnly && <Group justify="flex-end"><Button onClick={() => onSave(text.trim() || null)}>Übernehmen</Button></Group>}
    </Stack>
  );
}

function PasteModal({ opened, onClose, projectId, packageId, hasItems }: { opened: boolean; onClose: () => void; projectId: string; packageId: string; hasItems: boolean }): JSX.Element {
  const [text, setText] = useState('');
  const [replace, setReplace] = useState(false);
  const parsed = useMemo(() => parsePastedBoq(text), [text]);
  const run = useTenderMutation(projectId, packageId, () => tenderApi.importItems(projectId, packageId, parsed.rows, replace));
  const close = (): void => { setText(''); setReplace(false); run.reset(); onClose(); };
  return (
    <Modal opened={opened} onClose={close} title="LV aus einer Tabelle einfügen" size="xl">
      <Stack gap="sm">
        <Text size="sm">In Excel die Zeilen markieren, kopieren und hier einfügen. Spalten in dieser Reihenfolge:</Text>
        <List size="sm" type="ordered" spacing={0}>
          <List.Item>OZ</List.Item>
          <List.Item>Kurztext</List.Item>
          <List.Item>Menge – leer lassen für einen Titel</List.Item>
          <List.Item>Einheit</List.Item>
          <List.Item>geschätzter EP (optional)</List.Item>
          <List.Item>Langtext (optional)</List.Item>
        </List>
        <Text size="xs" c="dimmed">Ein Titel, dessen OZ die eines Titels darüber fortsetzt (01.02 nach 01), wird dessen Untertitel.</Text>
        <Textarea autosize minRows={8} maxRows={16} value={text} onChange={(e) => setText(e.currentTarget.value)} styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }} placeholder={'01\tWände\n01.0010\tStänderwand, 100 mm\t240\tm²\t58'} />
        {text.trim() && (
          <Text size="sm">
            Erkannt: {parsed.rows.filter((r) => r.kind === 'title').length} Titel, {parsed.rows.filter((r) => r.kind === 'position').length} Positionen.
            {parsed.skipped.length > 0 && <Text span c="orange.7"> Nicht lesbar und übersprungen: Zeile {parsed.skipped.join(', ')}.</Text>}
          </Text>
        )}
        {hasItems && <Checkbox label="Bisheriges LV ersetzen (sonst werden die Zeilen angehängt)" checked={replace} onChange={(e) => setReplace(e.currentTarget.checked)} />}
        {run.isError && <Alert color="red">{errorText(run.error)}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={close}>Abbrechen</Button>
          <Button disabled={parsed.rows.length === 0} loading={run.isPending} onClick={() => run.mutate(undefined, { onSuccess: close })}>
            {parsed.rows.length} Zeilen übernehmen
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
